import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CoinView } from './model';
import {
  betSnapshot,
  newBets,
  createBetBursts,
  MAX_BET_BURSTS,
  type BetBurst,
  type BetSnapshot,
} from './betBurstModel';

export function useBetBursts(coin: CoinView, now: number, enabled: boolean) {
  const [allowed, setAllowed] = useState(true);
  const [bursts, setBursts] = useState<BetBurst[]>([]);
  const previous = useRef<BetSnapshot | null>(null);
  const serial = useRef(0);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      // Forget the baseline on both hide and resume; never replay hidden trades.
      previous.current = null;
      setBursts([]);
      setAllowed(!reduced.matches && document.visibilityState === 'visible');
    };
    sync();
    reduced.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      reduced.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);
  const snapshot = betSnapshot(coin, now, enabled && allowed);
  const {
    id,
    simulated,
    votes,
    votesUp,
    stale,
    choice,
    phase,
    start,
    closesAt,
  } = coin;
  useLayoutEffect(() => {
    const next = betSnapshot(
      { id, simulated, votes, votesUp, stale, choice, phase, start, closesAt },
      now,
      enabled && allowed,
    );
    const last = previous.current;
    previous.current = next;
    if (
      !next ||
      !last ||
      last.id !== next.id ||
      last.simulated !== next.simulated
    ) {
      setBursts((current) => (current.length ? [] : current));
      return;
    }
    const changes = newBets(last, next);
    if (!changes.length) return;
    const createdAt = Date.now();
    const incoming = createBetBursts(next, changes, serial.current, createdAt);
    serial.current += incoming.length;
    setBursts((current) =>
      [
        ...current.filter((burst) => burst.expiresAt > createdAt),
        ...incoming,
      ].slice(-MAX_BET_BURSTS),
    );
  }, [
    id,
    simulated,
    votes,
    votesUp,
    stale,
    choice,
    phase,
    start,
    closesAt,
    now,
    enabled,
    allowed,
  ]);
  useEffect(() => {
    if (!bursts.length) return;
    const expiresAt = Math.min(...bursts.map((burst) => burst.expiresAt));
    const timer = setTimeout(
      () =>
        setBursts((current) =>
          current.filter((burst) => burst.expiresAt > Date.now()),
        ),
      Math.max(0, expiresAt - Date.now()) + 40,
    );
    return () => clearTimeout(timer);
  }, [bursts]);
  return snapshot ? bursts.filter((burst) => burst.coinId === coin.id) : [];
}
